import { UserAccountType, UserStatus } from "@authory-packages/core";
import { omit } from "lodash";
import { NextPageContext } from "next";
import { useRouter } from "next/router";
import { setCookie } from "nookies";
import { QueryClient, useQuery, dehydrate } from "@tanstack/react-query";
import { getPublicUser } from "../../api/user";
import { CookieKeys } from "../../types/cookie-keys";
import { getPublicUserQuerie } from "../../types/queries";

const Invite = () => {
    const router = useRouter();
    const author = router.query.author as string;
    const key = router.query.key as string;

    // Get the author
    const authorData = useQuery({
        queryKey: getPublicUserQuerie(author),
        queryFn: () => getPublicUser(author)
    });

    const isBusinessParent = authorData.data?.type === UserAccountType.BusinessParent;
    const inviteCookieDuration = 2 * 60 * 60; // 2 hours
    const formattedName = `${authorData.data?.firstName || ""} ${authorData.data?.lastName || ""}`;

    const trimmedParams = omit(router.query, ["key", "author"]);
    const trimmedUrlParams = Object.keys(trimmedParams)
        .map((key) => `${key}=${trimmedParams[key]}`)
        .join("&");

    if (process.browser) {
        setCookie(null, isBusinessParent ? CookieKeys.business_key : CookieKeys.referral_key, key, {
            path: "/",
            maxAge: inviteCookieDuration
        });

        setCookie(null, isBusinessParent ? CookieKeys.business_name : CookieKeys.referral_name, formattedName, {
            path: "/",
            maxAge: inviteCookieDuration
        });

        setCookie(null, isBusinessParent ? CookieKeys.business_user : CookieKeys.referral_user, author, {
            path: "/",
            maxAge: inviteCookieDuration
        });

        document.location.href = `/${trimmedUrlParams.length ? `?${trimmedUrlParams}` : ""}`;
    }

    return null;
};

export const getServerSideProps = async (ctx: NextPageContext) => {
    const queryClient = new QueryClient();

    const { author } = ctx.query;

    if (!Array.isArray(author)) {
        // If we fail to get author, send 404
        try {
            const authorData = await queryClient.fetchQuery({
                queryKey: getPublicUserQuerie(author!),
                queryFn: () => getPublicUser(author!)
            });

            if (authorData.status === UserStatus.Inactive && ctx.res) {
                ctx.res.statusCode = 404;
            }
        } catch (error) {
            if (ctx.res) {
                ctx.res.statusCode = 404;
            }
        }
    }

    return {
        props: {
            dehydratedState: dehydrate(queryClient),
            statusCode: ctx.res?.statusCode
        }
    };
};

export default Invite;
